import type { GeoPoint, Location } from '@/types';

// Google Maps API service
export const mapsService = {
  map: null as google.maps.Map | null,
  markers: new Map<string, google.maps.Marker>(),
  directionsService: null as google.maps.DirectionsService | null,
  directionsRenderer: null as google.maps.DirectionsRenderer | null,
  watchId: null as number | null,
  currentPosition: null as GeolocationPosition | null,

  // Initialize map
  initMap(containerId: string, center: GeoPoint, zoom: number = 14): google.maps.Map {
    const container = document.getElementById(containerId);
    if (!container) throw new Error('Map container not found');

    this.map = new google.maps.Map(container, {
      center: { lat: center.lat, lng: center.lng },
      zoom,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
      },
    });

    this.directionsService = new google.maps.DirectionsService();
    this.directionsRenderer = new google.maps.DirectionsRenderer({
      map: this.map,
      suppressMarkers: true,
    });

    return this.map;
  },

  // Get current position
  async getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.currentPosition = position;
          resolve(position);
        },
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  },

  // Start watching position
  startLocationTracking(
    onPosition: (position: GeolocationPosition) => void,
    onError?: (error: GeolocationPositionError) => void
  ): void {
    if (!navigator.geolocation) {
      onError?.({ message: 'Geolocation not supported' } as GeolocationPositionError);
      return;
    }

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.currentPosition = position;
        onPosition(position);
      },
      (error) => onError?.(error),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  },

  // Stop watching position
  stopLocationTracking(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  },

  // Add marker
  addMarker(
    id: string,
    position: GeoPoint,
    options?: {
      icon?: string;
      title?: string;
      draggable?: boolean;
    }
  ): google.maps.Marker {
    if (!this.map) throw new Error('Map not initialized');

    // Remove existing marker with same id
    this.removeMarker(id);

    const marker = new google.maps.Marker({
      position: { lat: position.lat, lng: position.lng },
      map: this.map,
      title: options?.title,
      draggable: options?.draggable || false,
      icon: options?.icon,
    });

    this.markers.set(id, marker);
    return marker;
  },

  // Update marker position
  updateMarkerPosition(id: string, position: GeoPoint): void {
    const marker = this.markers.get(id);
    if (marker) {
      marker.setPosition({ lat: position.lat, lng: position.lng });
    }
  },

  // Remove marker
  removeMarker(id: string): void {
    const marker = this.markers.get(id);
    if (marker) {
      marker.setMap(null);
      this.markers.delete(id);
    }
  },

  // Remove all markers
  removeAllMarkers(): void {
    this.markers.forEach((marker) => marker.setMap(null));
    this.markers.clear();
  },

  // Center map on position
  centerOnPosition(position: GeoPoint): void {
    if (this.map) {
      this.map.panTo({ lat: position.lat, lng: position.lng });
    }
  },

  // Fit bounds to show all markers
  fitBounds(): void {
    if (!this.map || this.markers.size === 0) return;

    const bounds = new google.maps.LatLngBounds();
    this.markers.forEach((marker) => {
      bounds.extend(marker.getPosition()!);
    });
    this.map.fitBounds(bounds);
  },

  // Geocode address to coordinates
  async geocodeAddress(address: string): Promise<GeoPoint | null> {
    const geocoder = new google.maps.Geocoder();
    
    try {
      const result = await geocoder.geocode({ address });
      if (result.results.length > 0) {
        const location = result.results[0].geometry.location;
        return {
          lat: location.lat(),
          lng: location.lng(),
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  },

  // Reverse geocode coordinates to address
  async reverseGeocode(lat: number, lng: number): Promise<string | null> {
    const geocoder = new google.maps.Geocoder();
    
    try {
      const result = await geocoder.geocode({
        location: { lat, lng },
      });
      if (result.results.length > 0) {
        return result.results[0].formatted_address;
      }
      return null;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  },

  // Search places
  async searchPlaces(
    query: string,
    location?: GeoPoint,
    radius: number = 5000
  ): Promise<Location[]> {
    const service = new google.maps.places.PlacesService(this.map!);
    
    return new Promise((resolve, reject) => {
      const request: google.maps.places.TextSearchRequest = {
        query,
        location: location ? new google.maps.LatLng(location.lat, location.lng) : undefined,
        radius,
      };

      service.textSearch(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          const locations: Location[] = results.map((place) => ({
            id: place.place_id!,
            name: place.name,
            address: place.formatted_address || '',
            coordinates: {
              lat: place.geometry!.location!.lat(),
              lng: place.geometry!.location!.lng(),
            },
          }));
          resolve(locations);
        } else {
          resolve([]);
        }
      });
    });
  },

  // Calculate route
  async calculateRoute(
    origin: GeoPoint,
    destination: GeoPoint,
    waypoints?: GeoPoint[]
  ): Promise<{
    distance: number;
    duration: number;
    path: GeoPoint[];
  } | null> {
    if (!this.directionsService) return null;

    const request: google.maps.DirectionsRequest = {
      origin: { lat: origin.lat, lng: origin.lng },
      destination: { lat: destination.lat, lng: destination.lng },
      waypoints: waypoints?.map((wp) => ({
        location: { lat: wp.lat, lng: wp.lng },
        stopover: true,
      })),
      optimizeWaypoints: true,
      travelMode: google.maps.TravelMode.DRIVING,
    };

    try {
      const result = await this.directionsService.route(request);
      if (result.routes.length > 0) {
        const route = result.routes[0];
        const leg = route.legs[0];
        
        // Extract path points
        const path: GeoPoint[] = [];
        route.overview_path.forEach((point) => {
          path.push({ lat: point.lat(), lng: point.lng() });
        });

        return {
          distance: leg.distance!.value / 1000, // Convert to km
          duration: leg.duration!.value / 60, // Convert to minutes
          path,
        };
      }
      return null;
    } catch (error) {
      console.error('Route calculation error:', error);
      return null;
    }
  },

  // Display route on map
  displayRoute(
    origin: GeoPoint,
    destination: GeoPoint,
    waypoints?: GeoPoint[]
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.directionsService || !this.directionsRenderer) {
        reject(new Error('Directions not initialized'));
        return;
      }

      const request: google.maps.DirectionsRequest = {
        origin: { lat: origin.lat, lng: origin.lng },
        destination: { lat: destination.lat, lng: destination.lng },
        waypoints: waypoints?.map((wp) => ({
          location: { lat: wp.lat, lng: wp.lng },
          stopover: true,
        })),
        optimizeWaypoints: true,
        travelMode: google.maps.TravelMode.DRIVING,
      };

      this.directionsService.route(request, (result, status) => {
        if (status === google.maps.DirectionsStatus.OK) {
          this.directionsRenderer!.setDirections(result);
          resolve();
        } else {
          reject(new Error(`Directions request failed: ${status}`));
        }
      });
    });
  },

  // Clear route
  clearRoute(): void {
    if (this.directionsRenderer) {
      this.directionsRenderer.setDirections({ routes: [] } as any);
    }
  },

  // Calculate estimated price
  calculatePrice(distanceKm: number, durationMinutes: number, rideType: string = 'economy'): number {
    const basePrice = 500;
    const pricePerKm = 150;
    const pricePerMinute = 10;

    let price = basePrice + distanceKm * pricePerKm + durationMinutes * pricePerMinute;

    switch (rideType) {
      case 'comfort':
        price *= 1.3;
        break;
      case 'premium':
        price *= 1.8;
        break;
      case 'family':
        price *= 1.5;
        break;
      default:
        break;
    }

    return Math.round(price);
  },

  // Get address from coordinates (simplified)
  async getAddressFromCoordinates(lat: number, lng: number): Promise<string> {
    const address = await this.reverseGeocode(lat, lng);
    return address || 'موقع غير معروف';
  },

  // Create custom marker icon
  createMarkerIcon(
    color: string,
    label?: string
  ): google.maps.Symbol {
    return {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#fff',
      strokeWeight: 2,
      scale: 12,
    };
  },

  // Add pulse effect to marker
  addPulseEffect(markerId: string): void {
    const marker = this.markers.get(markerId);
    if (!marker) return;

    // Create pulse circle
    const circle = new google.maps.Circle({
      map: this.map,
      center: marker.getPosition()!,
      radius: 100,
      fillColor: '#0066FF',
      fillOpacity: 0.3,
      strokeColor: '#0066FF',
      strokeOpacity: 0.8,
      strokeWeight: 2,
    });

    // Animate pulse
    let radius = 100;
    let opacity = 0.3;
    const animate = () => {
      radius += 10;
      opacity -= 0.01;
      
      if (radius > 300 || opacity <= 0) {
        radius = 100;
        opacity = 0.3;
      }
      
      circle.setRadius(radius);
      circle.setOptions({
        fillOpacity: opacity,
        strokeOpacity: opacity * 2,
      });
      
      requestAnimationFrame(animate);
    };
    
    animate();
  },

  // Get distance between two points
  getDistance(from: GeoPoint, to: GeoPoint): number {
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(to.lat - from.lat);
    const dLng = this.toRad(to.lng - from.lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(from.lat)) *
        Math.cos(this.toRad(to.lat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  },

  toRad(value: number): number {
    return (value * Math.PI) / 180;
  },
};

export default mapsService;
